<template>
  <div
    v-if="user.id || user.userName"
    class="wrap user-item"
    :class="{ 'user-item-wp': user.avatar_url }"
    :style="{ position: 'relative', maxWidth: itemMaxWidth || '130px' }"
  >
    <router-link
      :to="{ name: 'profile', params: { id: user.id, type: user.type } }"
      custom
      v-slot="{ navigate, href, route }"
    >
      <a
        :href="href"
        target="_blank"
        @click="navigate"
        class="d-flex user-link"
      >
        <template v-if="view === 'name-only'">
          <div
            class="user-name text-center"
            style="white-space: normal"
            :disabled="!userTooltip"
            v-b-tooltip.hover.html="{ variant: 'base', placement: 'top', placement:'top', boundary:'viewport' }"
            :title="userTooltip ? getUserTooltip : null"
          >
            {{ user.name || user.userName }}
          </div>
        </template>
        <template v-else>
          <b-img
            v-if="user.avatar_url"
            :src="user.avatar_url"
            rounded="circle"
            :alt="`${user.name || user.userName} profile image`"
            class="user-profile-avatar mr-10"
            @error="handleProfileImageLoadError"
          />
          <b-img
            v-else
            src="/images/avatar-placeholder.png"
            rounded="circle"
            alt="Failed to load profile image"
            class="user-profile-avatar mr-10"
          />

          <div>
            <div
              class="user-name"
              :disabled="!userTooltip"
              v-b-tooltip.hover.html="{ variant: 'base', placement: 'top', placement:'top', boundary:'viewport' }"
              :title="userTooltip ? getUserTooltip : null"
            >
              {{ user.name || user.userName }}
            </div>
            <div class="wrap space-between">
              <div class="user-position">
                {{ user.position || user.userPosition }}
              </div>

              <span class="user-icon" v-if="user.employment_type">
                  {{ icon }}
              </span>
            </div>
          </div>
        </template>
      </a>
    </router-link>
    <TextEditor
      :class="{ 'work-schedule-style': isWorkSchedulePage }"
      class="position-absolute user-item__editor"
      v-if="commentEditor && $can(permissions.ReadAllComments)"
      :entity-type="commentEditorOptions.entity_type"
      :entity-id="user.id"
      :data-already-loaded="commentEditorOptions.data_already_loaded"
    />
  </div>
  <div v-else class="simple-name">{{ user.name }}</div>
</template>

<script>
import { commentEntityTypes } from '@mocks/comment-entity-types'
import TextEditor from '@components/TextEditor'
import { permissions } from '@mocks/permissions'

export default {
  name: 'TableUserItem',
  components: {
    TextEditor
  },
  props: {
    user: {
      type: Object,
      required: true
    },
    view: {
      type: String,
      required: false
    },
    number: {
      type: Number,
      required: false
    },
    userTooltip: {
      type: Boolean,
      default: true
    },
    itemMaxWidth: {
      default: false
    },
    commentEditor: {
      type: Boolean,
      default: false
    },
    commentEditorOptions: {
      type: Object,
      required: false,
      default: () => {
        return {
          entity_type: commentEntityTypes.TableUserItem,
          data_already_loaded: false
        }
      }
    }
  },
  data () {
    return {
      permissions,
      profileImageLoadFailed: false
    }
  },
  watch: {
    number (v) {
      this.number = v
    }
  },
  computed: {
    icon () {
      switch (this.user.employment_type.type) {
        case 'team_member':
          return 'T'
        case 'independent':
          return 'I'
        case 'partner':
          return 'P'
        default:
          return ''
      }
    },
    isWorkSchedulePage () {
      return this.$route.path === '/booking-view'
    },
    paymentTypeLabel () {
      return (
        this.user.payment_type &&
        this.user.payment_type[0].toUpperCase() +
          this.user.payment_type.slice(1)
      )
    },
    employmentTypeLabel () {
      if (!this.user.employment_type) {
        return ''
      }
      return (
        this.user.employment_type.label +
        (this.user.employment_type.type === 'partner'
          ? ` (${this.user.employment_type.partner_name})`
          : '')
      )
    },
    getUserTooltip () {
      const userName = this.user.name || this.user.userName
      const userPosition = this.user.position || this.user.userPosition
      let extraInfo = ''
      if (this.user.employment_type) {
        extraInfo = ` <br/> ${this.employmentTypeLabel || ''} / ${
          this.paymentTypeLabel || ''
        }`
      }
      return (
        `<div class="text-left">${userName || ''} <br/> ${
          userPosition || ''
        } ${extraInfo}</div>` || ''
      )
    }
  },

  methods: {
    handleProfileImageLoadError () {
      this.profileImageLoadFailed = true
    }
  }
}
</script>

<style scoped lang="scss">
.wrap {
  width: 100%;
  display: flex;
  align-items: center;
}

.user-item {
  max-width: 130px;

  &-wp {
    max-width: 200px !important;
  }

  &__editor {
    top: 0;
    right: 0;
  }
}

.space-between {
  justify-content: space-between;
}

.user-name {
  font-size: 13px;
  color: $tableTextHeadColor2;
  // color: red;
  margin-bottom: 2px;
  text-overflow: ellipsis;
  overflow: hidden;
  max-width: 130px;
  white-space: nowrap;
}

.user-position {
  font-size: 11px;
  color: $tableTextHeadColor;
  text-overflow: ellipsis;
  overflow: hidden;
  max-width: 100px;
  white-space: nowrap;
  min-height: 16px;
}

.user-link:hover {
  text-decoration: none;
}

.user-icon {
  border-radius: 20px;
  border: 1px solid $bgColorBase;
  align-items: center;
  color: $fontColorAccent;
  display: flex;
  justify-content: center;
  font-size: 11.56px;
  height: 16.39px;
  width: 16px;
  position: absolute;
  left: 20px;
  background: #fff;
}

.green-icon-color {
  color: $colorGreen;
}

.amber-icon-color {
  color: $colorAmber;
}

.blue-icon-color {
  color: $colorBlue;
}

.mr-10 {
  margin-right: 10px;
}

.ml-10 {
  margin-left: 10px;
}

div.tooltip.b-tooltip-base .tooltip-inner {
  text-align: left;
}

.simple-name {
  font-size: 13px;
}

.work-schedule-style {
  top: 10px;
}
</style>
